import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend,
} from "recharts";
import { ArrowLeft, Clock, TrendingUp, Euro } from "lucide-react";

const COLORS = ["#002FA7", "#00A859", "#F59E0B", "#B429A6", "#FF2A2A"];
const scoreColor = (s) => (s >= 70 ? "text-[#00A859]" : s >= 40 ? "text-[#F59E0B]" : "text-destructive");

const KPI_ICONS = { "Ore risparmiate / mese": Clock, "Aumento conversion": TrendingUp, "Ricavi aggiuntivi": Euro };

export default function ReadinessCompare() {
  const [items, setItems] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selected, setSelected] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/readiness"), api.get("/readiness/framework")]).then(([a, f]) => {
      setItems(a.data);
      setAreas(f.data.areas);
      setSelected(a.data.slice(0, 3).map((x) => x.id));
    });
  }, []);

  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length >= 5 ? p : [...p, id]));
  const chosen = items.filter((x) => selected.includes(x.id));

  const radarData = areas.map((a) => {
    const row = { area: a.name };
    chosen.forEach((c) => { row[c.client_name] = (c.breakdown || []).find((b) => b.key === a.key)?.score ?? 0; });
    return row;
  });

  // KPI aggregate across chosen clients, grouped by KPI name
  const kpiAgg = {};
  chosen.forEach((c) => {
    (c.kpis || []).forEach((k) => {
      if (!kpiAgg[k.name]) kpiAgg[k.name] = { name: k.name, unit: k.unit, total: 0, clients: 0 };
      kpiAgg[k.name].total += Number(k.improvement || 0);
      kpiAgg[k.name].clients += 1;
    });
  });
  const kpiCards = Object.values(kpiAgg);

  return (
    <Layout>
      <PageHeader title="Confronto clienti" subtitle="Readiness e risultati a confronto">
        <Button variant="outline" className="rounded-none" onClick={() => navigate("/readiness")}><ArrowLeft className="w-4 h-4 mr-2" /> Indietro</Button>
      </PageHeader>

      <div className="p-8 grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-border p-4" data-testid="compare-client-selector">
          <div className="data-label mb-3">Seleziona clienti (max 5)</div>
          <div className="space-y-2 max-h-[360px] overflow-y-auto">
            {items.map((c) => (
              <label key={c.id} className="flex items-center gap-3 text-sm cursor-pointer py-1">
                <Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggle(c.id)} />
                <span className="flex-1 truncate">{c.client_name}</span>
                <span className={`font-mono text-xs ${scoreColor(c.overall_score)}`}>{c.overall_score}</span>
              </label>
            ))}
            {items.length === 0 && <p className="text-sm text-muted-foreground">Nessun assessment disponibile.</p>}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-border p-6" data-testid="compare-readiness-radar">
          <h3 className="font-heading font-bold text-lg tracking-tight mb-4">Profili di maturità</h3>
          {chosen.length === 0 ? <p className="text-sm text-muted-foreground py-12 text-center">Seleziona almeno un cliente.</p> : (
            <ResponsiveContainer width="100%" height={380}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#E5E7EB" />
                <PolarAngleAxis dataKey="area" tick={{ fontSize: 12, fill: "#4B5563" }} />
                <PolarRadiusAxis domain={[0, 5]} tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#9CA3AF" />
                {chosen.map((c, i) => <Radar key={c.id} name={c.client_name} dataKey={c.client_name} stroke={COLORS[i]} fill={COLORS[i]} fillOpacity={0.1} strokeWidth={2} />)}
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          )}
        </div>

        {kpiCards.length > 0 && (
          <div className="lg:col-span-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="kpi-aggregate">
            {kpiCards.map((k) => {
              const Icon = KPI_ICONS[k.name] || TrendingUp;
              return (
                <div key={k.name} className="bg-white border border-border p-6">
                  <div className="flex items-center justify-between">
                    <span className="data-label">{k.name}</span>
                    <Icon className="w-4 h-4 text-[#00A859]" />
                  </div>
                  <div className="font-mono font-medium text-3xl mt-3 text-[#00A859]">+{k.total.toLocaleString("it-IT")} <span className="text-base text-muted-foreground">{k.unit}</span></div>
                  <div className="text-xs text-muted-foreground mt-1">totale su {k.clients} clienti</div>
                </div>
              );
            })}
          </div>
        )}

        {chosen.length > 0 && (
          <div className="lg:col-span-4 bg-white border border-border overflow-x-auto" data-testid="compare-readiness-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="data-label px-4 py-3">Cliente</th>
                  <th className="data-label px-4 py-3">Settore</th>
                  {areas.map((a) => <th key={a.key} className="data-label px-4 py-3 text-right">{a.name.split(" ")[0]}</th>)}
                  <th className="data-label px-4 py-3 text-right">Totale</th>
                  <th className="data-label px-4 py-3">Offerta consigliata</th>
                </tr>
              </thead>
              <tbody>
                {chosen.map((c, i) => (
                  <tr key={c.id} className={`border-b border-border ${i % 2 ? "bg-[#F9FAFB]" : "bg-white"}`}>
                    <td className="px-4 py-2 font-medium">{c.client_name}</td>
                    <td className="px-4 py-2 text-muted-foreground">{c.sector || "—"}</td>
                    {areas.map((a) => <td key={a.key} className="px-4 py-2 text-right font-mono">{(c.breakdown || []).find((b) => b.key === a.key)?.score ?? 0}</td>)}
                    <td className={`px-4 py-2 text-right font-mono font-semibold ${scoreColor(c.overall_score)}`}>{c.overall_score}</td>
                    <td className="px-4 py-2">{c.ai_deliverable?.recommended_offer?.level || <span className="text-muted-foreground text-xs">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
